import { useState, useMemo } from 'react';
import { Shield, Users, BarChart3, ScrollText, Search, Cpu, Wrench, UsersRound, Globe, Trash2 } from 'lucide-react';
import { MetricsDashboard } from '@/components/admin/MetricsDashboard';
import { ModelsManagementPanel } from '@/components/admin/ModelsManagementPanel';
import { ToolsManagementPanel } from '@/components/admin/ToolsManagementPanel';
import { AuditLogsPanel } from '@/components/admin/AuditLogsPanel';
import { PlatformSettingsPanel } from '@/components/admin/PlatformSettingsPanel';
import { TeamsManagementPanel } from '@/components/admin/TeamsManagementPanel';
import { EspacoCaldeiraPanel } from '@/components/admin/EspacoCaldeiraPanel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/useProfile';
import { useNavigation } from '@/contexts/NavigationContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Tables } from '@/integrations/supabase/types';

type SubTab = 'users' | 'teams' | 'espaco' | 'models' | 'tools' | 'metrics' | 'logs' | 'settings';

const SUB_TABS: { id: SubTab; label: string; icon: typeof Users }[] = [
  { id: 'users', label: 'Usuários', icon: Users },
  { id: 'teams', label: 'Times', icon: UsersRound },
  { id: 'espaco', label: 'Espaço Caldeira', icon: Globe },
  { id: 'models', label: 'Modelos', icon: Cpu },
  { id: 'tools', label: 'Ferramentas', icon: Wrench },
  { id: 'metrics', label: 'Métricas', icon: BarChart3 },
  { id: 'logs', label: 'Logs', icon: ScrollText },
  { id: 'settings', label: 'Config', icon: Shield },
];

export const AdminPage = () => {
  const { profile, isAdmin, isLoading: profileLoading } = useProfile();
  const { navigateToGlobal } = useNavigation();

  if (profileLoading) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Shield className="h-12 w-12 text-tertiary mx-auto" />
          <h2 className="text-lg font-semibold text-primary">Acesso restrito</h2>
          <p className="text-secondary text-sm max-w-sm">
            Apenas administradores podem acessar esta página.
          </p>
          <Button variant="outline" onClick={navigateToGlobal}>
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return <AdminContent />;
};

const AdminContent = () => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('users');

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-8 pt-6 pb-4 space-y-4 shrink-0">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-brand-primary" />
          <h1 className="text-lg font-semibold text-primary">Administração</h1>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 bg-secondary rounded-lg p-1 w-fit">
          {SUB_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-surface text-primary shadow-sm'
                  : 'text-secondary hover:text-primary'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 pb-8">
        {activeSubTab === 'users' && <UsersTab />}
        {activeSubTab === 'teams' && <TeamsManagementPanel />}
        {activeSubTab === 'espaco' && <EspacoCaldeiraPanel />}
        {activeSubTab === 'models' && <ModelsManagementPanel />}
        {activeSubTab === 'tools' && <ToolsManagementPanel />}
        {activeSubTab === 'metrics' && <MetricsDashboard />}
        {activeSubTab === 'logs' && <AuditLogsPanel />}
        {activeSubTab === 'settings' && <PlatformSettingsPanel />}
      </div>
    </div>
  );
};

const PlaceholderTab = ({ icon: Icon, label }: { icon: typeof BarChart3; label: string }) => (
  <div className="flex-1 flex items-center justify-center py-24">
    <div className="text-center space-y-3">
      <Icon className="h-10 w-10 text-tertiary mx-auto" />
      <p className="text-secondary text-sm font-medium">{label} — Em breve</p>
    </div>
  </div>
);

const UsersTab = () => {
  const { user } = useAuth();
  
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Tables<'profiles'> | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: users = [], isLoading } = useQuery<Tables<'profiles'>[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.nome?.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  const adminCount = users.filter((u) => u.role === 'admin').length;

  const handleToggleRole = async (target: Tables<'profiles'>) => {
    const newRole = target.role === 'admin' ? 'membro' : 'admin';
    const { error } = await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', target.id);

    if (error) {
      toast.error(`Erro ao alterar role: ${error.message}`);
      return;
    }

    toast.success(newRole === 'admin' ? 'Promovido a Admin' : 'Rebaixado para Membro');
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    queryClient.invalidateQueries({ queryKey: ['profile'] });
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);

    try {
      const { error } = await supabase.functions.invoke('admin-delete-user', {
        body: { userId: deleteTarget.id },
      });
      if (error) throw error;

      toast.success('Usuário excluído permanentemente');
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (err: any) {
      toast.error(`Erro ao excluir usuário: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-secondary">
        <span>{users.length} usuários</span>
        <span>•</span>
        <span>{adminCount} admins</span>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
        <input
          type="text"
          placeholder="Buscar por nome ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg border border-color overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead className="w-12"></TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Membro desde</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => {
              const isSelf = u.id === user?.id;
              const initials = (u.nome || u.email)
                .split(' ')
                .map((n) => n[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();

              return (
                <TableRow key={u.id}>
                  <TableCell>
                    <Avatar className="h-8 w-8">
                      {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.nome || u.email} />}
                      <AvatarFallback className="text-xs bg-secondary text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-medium text-primary">{u.nome || '—'}</TableCell>
                  <TableCell className="text-secondary">{u.email}</TableCell>
                  <TableCell>
                    <Badge
                      variant={u.role === 'admin' ? 'default' : 'secondary'}
                      className={
                        u.role === 'admin'
                          ? 'bg-green-600/15 text-green-600 border-green-600/30 hover:bg-green-600/20'
                          : ''
                      }
                    >
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-secondary text-sm">
                    {new Date(u.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    {isSelf ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="p-1 text-tertiary cursor-not-allowed opacity-50" disabled>
                            •••
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Você não pode alterar seu próprio role</TooltipContent>
                      </Tooltip>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 text-secondary hover:text-primary transition-colors">
                            •••
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleToggleRole(u)}>
                            {u.role === 'admin' ? 'Rebaixar para Membro' : 'Promover a Admin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteTarget(u)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir usuário
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-secondary py-8">
                  Nenhum usuário encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário permanentemente</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove o usuário <strong>{deleteTarget?.email}</strong> do Auth, apaga o perfil e remove dados associados, incluindo conversas, projetos próprios, recursos pessoais, arquivos e vínculos de times/projetos. Logs e sessões permanecem imutáveis.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Excluindo...' : 'Excluir permanentemente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
